import React from 'react';
import { Campaign } from '../../types/Campaign';
import { CalendarIcon, DollarSignIcon, TagIcon, PercentIcon, ArrowRightIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="overflow-x-auto" data-id="element-1379">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1380">
        <thead className="bg-gray-50" data-id="element-1381">
          <tr data-id="element-1382">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1383">
              Campaign
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1384">
              Code
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1385">
              Discount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1386">
              Budget Usage
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1387">
              Expiry
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1388">
              Status
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-1389">
              <span className="sr-only" data-id="element-1390">View</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1391">
          {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onCampaignClick(campaign.id)} data-id="element-1392">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1393">
                  <div className="flex items-center" data-id="element-1394">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1395">
                      <TagIcon size={18} className="text-blue-600" data-id="element-1396" />
                    </div>
                    <div className="ml-4" data-id="element-1397">
                      <div className="text-sm font-medium text-gray-900" data-id="element-1398">
                        {campaign.title}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1399">
                        {campaign.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1400">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1401">
                    {campaign.code}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1402">
                    {campaign.codeStrategy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1403">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-1404">
                    {campaign.discountPercentage > 0 ? <>
                        <PercentIcon size={16} className="mr-1 text-purple-500" data-id="element-1405" />
                        {campaign.discountPercentage}%
                      </> : <>
                        <DollarSignIcon size={16} className="mr-1 text-green-500" data-id="element-1406" />
                        {campaign.fixedDiscountAmount} {campaign.currency}
                      </>}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1407">
                    {campaign.discountCarriedBy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1408">
                  <div className="flex items-center" data-id="element-1409">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-1410">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{
                  width: `${calculateBudgetUsage(campaign)}%`
                }} data-id="element-1411"></div>
                    </div>
                    <span className="text-sm text-gray-500" data-id="element-1412">
                      {calculateBudgetUsage(campaign)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-1413">
                    {campaign.budgetUsed.toLocaleString()} /{' '}
                    {campaign.budget.toLocaleString()} {campaign.currency}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1414">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-1415">
                    <CalendarIcon size={16} className="mr-1 text-gray-500" data-id="element-1416" />
                    {campaign.expiryDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1417">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(campaign.status)}`} data-id="element-1418">
                    {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1419">
                  <button className="text-blue-600 hover:text-blue-900" data-id="element-1420">
                    <ArrowRightIcon size={16} data-id="element-1421" />
                  </button>
                </td>
              </tr>) : <tr data-id="element-1422">
              <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1423">
                No campaigns found
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
}